'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { InfoIcon, ShoppingBag } from 'lucide-react'
import { useTranslation } from 'react-i18next'

// ============================================================================
// Schema
// ============================================================================
//
// Wetracked auto-detects the Shopify shop domain from the page on which its
// scripts run, so it requires no API key, no pixel ID, no advertiser ID.
// We still keep an empty credentials object to match the shape every other
// integration's submit handler expects.
// ============================================================================

const wetrackedFormSchema = z.object({})

type FormValues = z.infer<typeof wetrackedFormSchema>

interface WetrackedFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function WetrackedForm({
  onSubmit,
  isLoading = false,
}: WetrackedFormProps) {
  const { t } = useTranslation('integrations')

  const form = useForm<FormValues>({
    resolver: zodResolver(wetrackedFormSchema),
    defaultValues: {},
  })

  const { handleSubmit } = form

  const handleFormSubmit = async () => {
    // Wetracked stores no credentials; submit empty payload so the integration
    // row is created/updated and the `enabled` toggle from the parent header
    // is what actually drives behavior.
    await onSubmit({
      credentials: {},
      settings: {},
    })
  }

  return (
    <form
      id="integration-config-form"
      onSubmit={handleSubmit(handleFormSubmit)}
      className="space-y-6"
    >
      {/* About */}
      <Alert>
        <InfoIcon className="h-4 w-4" />
        <AlertTitle>{t('wetracked.aboutTitle')}</AlertTitle>
        <AlertDescription>
          {t('wetracked.aboutDescription')}
        </AlertDescription>
      </Alert>

      {/* Shopify-only callout */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShoppingBag className="h-4 w-4" />
            {t('wetracked.shopifyOnlyTitle')}
          </CardTitle>
          <CardDescription>
            {t('wetracked.shopifyOnlyDescription')}
          </CardDescription>
        </CardHeader>
      </Card>

      {/* What it does */}
      <Card>
        <CardHeader>
          <CardTitle>{t('wetracked.whatItDoesTitle')}</CardTitle>
          <CardDescription>
            {t('wetracked.whatItDoesDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
            <li>
              {t('wetracked.bullet1')}
            </li>
            <li>
              {t('wetracked.bullet2')}
            </li>
            <li>
              {t('wetracked.bullet3')}
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Setup instructions */}
      <Card>
        <CardHeader>
          <CardTitle>{t('wetracked.setupTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg bg-muted p-4">
            <ol className="list-inside list-decimal space-y-2 text-sm text-muted-foreground">
              <li>
                {t('wetracked.step1')}
              </li>
              <li>
                {t('wetracked.step2')}
              </li>
              <li>
                {t('wetracked.step3')}
              </li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </form>
  )
}
